import React, { useState, useEffect, useRef, useMemo, useContext, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { IconX, IconSparkles } from '@tabler/icons-react'
import { notifications } from 'context'
import { getSchoolTerms, getMyGroups } from '../../../service/projects'
import { useAIGeneration } from '../../../hooks/useAIGeneration'
import { StepperContext } from '../../../context/StepperContext'

interface UseAIGeneratorModalProps {
  opened: boolean
  projectId: string | null
  onClose: () => void
  onGenerate: (data: any) => void
}

interface UseAIGeneratorModalReturn {
  selectedSchoolTermId: string | null
  selectedGroupId: string | null
  description: string
  schoolTermsData: any[]
  groupsData: any[]
  loadingGroups: boolean
  generating: boolean
  generatingMessage: string
  generatingProgress: number
  setSelectedGroupId: (value: string | null) => void
  setDescription: (value: string) => void
  handleSchoolTermChange: (value: string | null) => void
  handleGenerate: () => void
  handleClose: () => void
}

export const useAIGeneratorModal = ({
  opened,
  projectId,
  onClose,
  onGenerate
}: UseAIGeneratorModalProps): UseAIGeneratorModalReturn => {
  const { t } = useTranslation()
  const { generateProject } = useAIGeneration()
  const { aiContext, updateAIContext } = useContext(StepperContext)

  const [selectedSchoolTermId, setSelectedSchoolTermId] = useState<string | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [schoolTermsData, setSchoolTermsData] = useState<any[]>([])
  const [groupsData, setGroupsData] = useState<any[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatingMessage, setGeneratingMessage] = useState('')
  const [generatingProgress, setGeneratingProgress] = useState(0)

  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const updateAIContextRef = useRef(updateAIContext)
  updateAIContextRef.current = updateAIContext

  useEffect(() => {
    if (opened && aiContext) {
      if (aiContext.schoolTermId) setSelectedSchoolTermId(aiContext.schoolTermId)
      if (aiContext.groupId) setSelectedGroupId(aiContext.groupId)
      if (aiContext.description) setDescription(aiContext.description)
    }
  }, [opened, aiContext])

  useEffect(() => {
    if (opened) {
      const idToUse = projectId || 'new'
      getSchoolTerms(idToUse).then(res => {
        if (res !== undefined) {
          const terms = res instanceof Array ? res : [res]
          const formattedTerms = terms.map(term => ({
            value: term.value,
            label: term.current ? `${term.label} - ${t('new_project_page.current')}` : term.label
          }))
          setSchoolTermsData(formattedTerms)

          const currentTerm = terms.find(t => t.current)
          if (currentTerm && !selectedSchoolTermId) {
            setSelectedSchoolTermId(currentTerm.value)
          }
        }
      }).catch(() => {})
    }
  }, [opened, projectId, t, selectedSchoolTermId])

  useEffect(() => {
    if (selectedSchoolTermId) {
      setLoadingGroups(true)
      getMyGroups(selectedSchoolTermId).then(res => {
        if (res !== undefined) {
          setGroupsData(res.map(g => ({ value: g.value, label: g.label })))
        }
        setLoadingGroups(false)
      }).catch(() => {
        setLoadingGroups(false)
      })
    }
  }, [selectedSchoolTermId])

  const debouncedUpdateContext = useMemo(
    () => debounce((schoolTermId: string | null, groupId: string | null, desc: string) => {
      if (schoolTermId || groupId || desc) {
        updateAIContextRef.current({
          schoolTermId,
          groupId,
          description: desc,
        })
      }
    }, 1000),
    []
  )

  useEffect(() => {
    if (opened && !generating &&
        (selectedSchoolTermId !== aiContext?.schoolTermId ||
         selectedGroupId !== aiContext?.groupId ||
         description !== aiContext?.description)) {
      debouncedUpdateContext(selectedSchoolTermId, selectedGroupId, description)
    }

    return () => {
      debouncedUpdateContext.cancel()
    }
  }, [selectedSchoolTermId, selectedGroupId, description, opened, generating, aiContext, debouncedUpdateContext])

  useEffect(() => {
    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current)
      }
    }
  }, [])

  const handleGenerate = useCallback(async () => {
    if (!selectedSchoolTermId || !selectedGroupId || !description.trim()) {
      notifications.show({
        message: t('required_field'),
        color: 'red',
        icon: React.createElement(IconX, { size: 24 })
      })
      return
    }

    setGenerating(true)

    try {
      const messages = [
        t('new_project_page.ai_analyzing_request'),
        t('new_project_page.ai_researching_content'),
        t('new_project_page.ai_structuring_objectives'),
        t('new_project_page.ai_defining_stages'),
        t('new_project_page.ai_creating_activities'),
        t('new_project_page.ai_finalizing_project')
      ]

      const selectedGroup = groupsData.find((g: any) => g.value === selectedGroupId)

      let messageIndex = 0
      let progress = 0
      messageIntervalRef.current = setInterval(() => {
        setGeneratingMessage(messages[messageIndex])
        messageIndex = (messageIndex + 1) % messages.length
        progress = Math.min(progress + 12, 90)
        setGeneratingProgress(progress)
      }, 2000)

      const result = await generateProject(description, selectedGroup)

      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current)
        messageIntervalRef.current = null
      }
      setGeneratingMessage('Finalizando geração...')
      setGeneratingProgress(100)

      await new Promise(resolve => setTimeout(resolve, 800))

      updateAIContext({
        schoolTermId: selectedSchoolTermId,
        groupId: selectedGroupId,
        description,
        generatedData: result.formData,
        fullApiResponse: result.apiResponse,
        projectItems: result.apiResponse.project_items,
      })

      onGenerate(result.formData)

      notifications.show({
        message: t('new_project_page.ai_generation_success'),
        color: 'green',
        icon: React.createElement(IconSparkles, { size: 24 })
      })

      onClose()
    } catch (error) {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current)
        messageIntervalRef.current = null
      }

      notifications.show({
        message: t('new_project_page.ai_generation_error'),
        color: 'red',
        icon: React.createElement(IconX, { size: 24 })
      })
    } finally {
      setGenerating(false)
      setGeneratingMessage('')
      setGeneratingProgress(0)
    }
  }, [selectedSchoolTermId, selectedGroupId, description, t, groupsData, generateProject, updateAIContext, onGenerate, onClose])

  const handleClose = useCallback(() => {
    if (!generating) {
      setGeneratingMessage('')
      setGeneratingProgress(0)
      onClose()
    }
  }, [generating, onClose])

  const handleSchoolTermChange = useCallback((value: string | null) => {
    setSelectedSchoolTermId(value)
    setSelectedGroupId(null)
  }, [])

  const handleSetDescription = useCallback((value: string) => {
    setDescription(value)
  }, [])

  const handleSetSelectedGroupId = useCallback((value: string | null) => {
    setSelectedGroupId(value)
  }, [])

  return {
    selectedSchoolTermId,
    selectedGroupId,
    description,
    schoolTermsData,
    groupsData,
    loadingGroups,
    generating,
    generatingMessage,
    generatingProgress,
    setSelectedGroupId: handleSetSelectedGroupId,
    setDescription: handleSetDescription,
    handleSchoolTermChange,
    handleGenerate,
    handleClose,
  }
}